import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import * as publicidadApi from '@/api/publicidad.api';
import type { FilaDeAnuncio } from '@/api/publicidad.api';
import * as eficienciaApi from '@/api/eficiencia.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { money } from '@/lib/money';

/**
 * PUBLICIDAD — de dónde salen los clientes y qué anuncio paga.
 *
 * Portado de la parte de anuncios de Supervisión de Tony Boga (4-nov-2026),
 * con los componentes de Osito.
 *
 * Una venta aquí es un comprobante de pago mandado por el cliente. Si pagó en
 * la tienda, esa venta no está, así que los totales son un PISO. Donde no hay
 * dato va «—»: un cero dice «no vendió» y un guion dice «no se sabe».
 */

const PERIODOS = [
  { dias: 7, etiqueta: 'Últimos 7 días' },
  { dias: 30, etiqueta: 'Últimos 30 días' },
  { dias: 90, etiqueta: 'Últimos 90 días' },
];

const pesos = (n: number | null) => (n == null ? '—' : money(n, 'RD$'));
const pct = (n: number | null) => (n == null ? '—' : `${n} %`);
const veces = (n: number | null) => (n == null ? '—' : `${n.toLocaleString('es-DO')}×`);
const entero = (n: number) => n.toLocaleString('es-DO');

export default function Publicidad() {
  const [dias, setDias] = useState(30);
  const [editando, setEditando] = useState<FilaDeAnuncio | null>(null);
  const [costo, setCosto] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['publicidad', dias],
    queryFn: () => publicidadApi.getPublicidad(dias),
  });

  const guardar = useMutation({
    mutationFn: (d: { sourceId: string; titulo?: string; costo: number }) =>
      eficienciaApi.ponerCostoDeAnuncio(d),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['publicidad'] });
      void qc.invalidateQueries({ queryKey: ['eficiencia'] });
      setEditando(null);
      setCosto('');
    },
  });

  const totalClientes = data ? data.origen.anuncio + data.origen.organico : 0;
  const tasaAnuncio =
    data && data.origen.anuncio > 0
      ? Math.round((data.ventas.porAnuncio.ventas / data.origen.anuncio) * 1000) / 10
      : null;
  const tasaOrganico =
    data && data.origen.organico > 0
      ? Math.round((data.ventas.sinAnuncio.ventas / data.origen.organico) * 1000) / 10
      : null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Publicidad"
        description="De dónde salen los clientes y qué anuncio paga"
        action={
          <Select value={String(dias)} onChange={(e) => setDias(Number(e.target.value))}>
            {PERIODOS.map((p) => (
              <option key={p.dias} value={p.dias}>
                {p.etiqueta}
              </option>
            ))}
          </Select>
        }
      />

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { k: 'Clientes nuevos', v: entero(totalClientes) },
              { k: 'Entraron por anuncio', v: entero(data.origen.anuncio) },
              { k: 'Vendido por anuncio', v: money(data.ventas.porAnuncio.vendido, 'RD$') },
              { k: 'Invertido en anuncios', v: pesos(data.invertido) },
            ].map((c) => (
              <Card key={c.k}>
                <CardBody>
                  <p className="text-xs uppercase tracking-wide text-slate-500">{c.k}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{c.v}</p>
                </CardBody>
              </Card>
            ))}
          </div>

          <Card>
            <CardBody>
              <h2 className="mb-3 text-sm font-semibold">Anuncio contra los que llegan solos</h2>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-medium">Por dónde entró</th>
                      <th className="px-3 py-2 text-right font-medium">Clientes</th>
                      <th className="px-3 py-2 text-right font-medium">Compraron</th>
                      <th className="px-3 py-2 text-right font-medium">Tasa</th>
                      <th className="px-3 py-2 text-right font-medium">Vendido</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-surface-border/60">
                      <td className="px-3 py-2">Por un anuncio</td>
                      <td className="px-3 py-2 text-right tabular-nums">{entero(data.origen.anuncio)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {entero(data.ventas.porAnuncio.ventas)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(tasaAnuncio)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {money(data.ventas.porAnuncio.vendido, 'RD$')}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Llegaron solos</td>
                      <td className="px-3 py-2 text-right tabular-nums">{entero(data.origen.organico)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {entero(data.ventas.sinAnuncio.ventas)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(tasaOrganico)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {money(data.ventas.sinAnuncio.vendido, 'RD$')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h2 className="mb-3 text-sm font-semibold">Anuncio por anuncio</h2>
              {data.anuncios.length === 0 ? (
                <EmptyState title="Ningún cliente entró por un anuncio en este período" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-3 py-2 font-medium">Anuncio</th>
                        <th className="px-3 py-2 text-right font-medium">Clientes</th>
                        <th className="px-3 py-2 text-right font-medium">Compraron</th>
                        <th className="px-3 py-2 text-right font-medium">Tasa</th>
                        <th className="px-3 py-2 text-right font-medium">Vendido</th>
                        <th className="px-3 py-2 text-right font-medium">Costo</th>
                        <th className="px-3 py-2 text-right font-medium">Por venta</th>
                        <th className="px-3 py-2 text-right font-medium">Retorno</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.anuncios.map((a) => (
                        <tr
                          key={a.anuncioId ?? '__sin__'}
                          className="border-b border-surface-border/60 last:border-0"
                        >
                          <td className="px-3 py-2">
                            <span className="font-medium">{a.nombre}</span>
                            {a.plataforma && (
                              <span className="ml-2 text-xs text-slate-500">{a.plataforma}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{entero(a.clientes)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{entero(a.compraron)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(a.tasa)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money(a.vendido, 'RD$')}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pesos(a.costo)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pesos(a.costoPorVenta)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{veces(a.retorno)}</td>
                          <td className="px-3 py-2 text-right">
                            {a.anuncioId && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditando(a);
                                  setCosto(a.costo == null ? '' : String(a.costo));
                                }}
                                className="text-slate-400 hover:text-slate-700"
                                aria-label={`Anotar el costo de ${a.nombre}`}
                              >
                                <Pencil size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {data.anunciosSinCosto > 0 && (
                <p className="mt-3 text-sm text-slate-500">
                  {data.anunciosSinCosto}{' '}
                  {data.anunciosSinCosto === 1 ? 'anuncio trajo gente y no tiene' : 'anuncios trajeron gente y no tienen'}{' '}
                  costo anotado. Sin eso, la tabla dice cuántos trajo pero no si valió la pena.
                  Anótalo con el lápiz.
                </p>
              )}
            </CardBody>
          </Card>
        </>
      )}

      <Modal
        open={editando != null}
        onClose={() => setEditando(null)}
        title={editando ? `Costo de ${editando.nombre}` : ''}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Lo que se pagó por este anuncio en el período, en pesos enteros.
          </p>
          <Input
            type="number"
            min={0}
            step={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            placeholder="0"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              disabled={!/^\d+$/.test(costo) || guardar.isPending}
              onClick={() =>
                editando?.anuncioId &&
                guardar.mutate({
                  sourceId: editando.anuncioId,
                  titulo: editando.nombre,
                  costo: Number(costo),
                })
              }
            >
              Guardar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
